import { createHeadlessEditor } from "@lexical/headless";
import { $convertFromMarkdownString, TRANSFORMERS } from "@lexical/markdown";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $parseSerializedNode,
} from "lexical";
import { EDITOR_NODES_HEADLESS } from "../../utils/nodes-headless";

import type { LexicalEditor } from "lexical";

export function insert(
  editor: LexicalEditor,
  format: "email" | "chat" | "sms",
  newCopy: string,
): void {
  const otherEditor = createHeadlessEditor({
    nodes: EDITOR_NODES_HEADLESS[format],
  });

  otherEditor.update(() => $convertFromMarkdownString(newCopy, TRANSFORMERS), {
    discrete: true,
  });

  const otherEditorJson = otherEditor.getEditorState().toJSON();

  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    const nodes = selection.getNodes();
    const parent = nodes[0]!.getParent();

    if (parent === null) return;

    const prev = parent.getPreviousSibling();

    const mapped = otherEditorJson.root.children.map((node) => $parseSerializedNode(node));
    mapped.forEach((node) => node.markDirty());

    if (prev !== null) mapped.toReversed().forEach((node) => prev.insertAfter(node));
    else {
      const root = $getRoot();
      const firstChild = root.getChildren()[0];
      if (firstChild) mapped.forEach((node) => firstChild.insertBefore(node));
      else root.append(...mapped);
    }
  });
}

export function replaceSelection(
  editor: LexicalEditor,
  format: "email" | "chat" | "sms",
  newCopy: string,
): void {
  const otherEditor = createHeadlessEditor({
    nodes: EDITOR_NODES_HEADLESS[format],
  });

  otherEditor.update(() => $convertFromMarkdownString(newCopy, TRANSFORMERS), {
    discrete: true,
  });

  const otherEditorJson = otherEditor.getEditorState().toJSON();

  editor.update(() => {
    const highlighted = $getSelection();
    if (!$isRangeSelection(highlighted)) return;

    const nodes = highlighted.getNodes();
    const parent = nodes[0]!.getParent();
    if (parent === null) return;

    const prev = parent.getPreviousSibling();

    nodes.forEach((node) => {
      if ($isTextNode(node)) node.getParent()?.remove();
      else node.remove();
    });

    const mapped = otherEditorJson.root.children.map((node) => $parseSerializedNode(node));
    mapped.forEach((node) => node.markDirty());

    if (prev !== null) mapped.toReversed().forEach((node) => prev.insertAfter(node));
    else {
      const root = $getRoot();
      const firstChild = root.getChildren()[0];
      if (firstChild) mapped.forEach((node) => firstChild.insertBefore(node));
      else root.append(...mapped);
    }
  });
}
